/**
 * Audit Log Table
 *
 * Displays the audit log for feature assignment changes.
 */

import { useState, type ReactElement } from 'react';
import { useAuditLog } from '../../hooks/useAdminAssignments';

interface AuditLogTableProps {
  projectId: string;
}

export function AuditLogTable({ projectId }: AuditLogTableProps): ReactElement {
  const { data: entries, isLoading } = useAuditLog(projectId);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (isLoading) {
    return <div className="settings-loading">Loading audit log...</div>;
  }

  const logEntries = entries ?? [];

  return (
    <div className="audit-log">
      <h3 className="audit-log__title">Audit Log</h3>
      {logEntries.length === 0 ? (
        <p className="audit-log__empty">No audit entries for this project.</p>
      ) : (
        <table className="feature-table__table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Action</th>
              <th>Actor</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {logEntries.map((entry) => (
              <tr key={entry.id}>
                <td className="audit-log__timestamp">
                  {new Date(entry.created_at).toLocaleString()}
                </td>
                <td>{entry.action}</td>
                <td>{entry.actor}</td>
                <td>
                  <button
                    type="button"
                    className="feature-table__edit-btn"
                    onClick={() =>
                      { setExpandedId(
                        expandedId === entry.id ? null : entry.id,
                      ); }
                    }
                    aria-label="Toggle details"
                  >
                    {expandedId === entry.id ? 'Hide' : 'View'}
                  </button>
                  {expandedId === entry.id && (
                    <div className="audit-log__detail">
                      {entry.before_state && (
                        <div>
                          <strong>Before:</strong>
                          <pre>{JSON.stringify(entry.before_state, null, 2)}</pre>
                        </div>
                      )}
                      {entry.after_state && (
                        <div>
                          <strong>After:</strong>
                          <pre>{JSON.stringify(entry.after_state, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
